import { useEffect, useState } from "react";
import { PageShell, PolicySection } from "@/components/PageShell";
import { getContent, getPublicSettings } from "@/lib/api";

const SEED_TITLE = "About Findivine";
const SEED_BODY = "Findivine is a non-profit rooted in seva.";

export default function About() {
  const [cms, setCms] = useState(null);
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    getContent("about").then(setCms).catch(() => {});
    getPublicSettings().then(setSettings).catch(() => {});
  }, []);
  const customBody = cms?.body && cms.body.trim() && cms.body.trim() !== SEED_BODY;
  const whoTitle = customBody && cms?.title && cms.title.trim() !== SEED_TITLE ? cms.title : "Who we are";

  return (
    <PageShell
      title="About Us"
      testid="about-page"
      subtitle="Findivine — the giving platform of One Kind Earth Foundation, an NGO accepting donations for the welfare of society."
    >
      <PolicySection title={whoTitle}>
        {customBody ? (
          cms.body.trim().split(/\n\n+/).map((p, i) => <p key={i}>{p}</p>)
        ) : (
          <>
            <p>
              Findivine is a non-profit initiative of <strong>One Kind Earth Foundation</strong>, a non-governmental
              organisation (NGO) dedicated to the welfare of society. Guided by our belief in
              <em> Divinity in Humanity</em>, we work to serve people, animals and communities with dignity and compassion.
            </p>
            <p>
              Our mission is simple: <strong>to turn devotion into tangible relief</strong> — converting the goodwill of
              donors into practical support for those who need it most.
            </p>
          </>
        )}
      </PolicySection>

      <PolicySection title="What we do">
        <p>Findivine accepts donations for social welfare across five focus areas:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Women Empowerment</strong> — supporting the health, dignity and independence of women and girls.</li>
          <li><strong>Animal Welfare</strong> — caring for animals in need, including fodder and relief for cows.</li>
          <li><strong>Child Education</strong> — helping children in government schools with learning materials.</li>
          <li><strong>Economically Weaker Section Welfare</strong> — supporting families facing financial hardship.</li>
          <li><strong>Hindu Religious Empowerment</strong> — nurturing spiritual and cultural well-being.</li>
        </ul>
      </PolicySection>

      <PolicySection title="Our current initiatives">
        <ul className="list-disc pl-6 space-y-2">
          <li>Providing sanitary pads to women and girls in Rusirani Village, Rajasthan.</li>
          <li>Arranging fodder for cows across the city and nearby areas.</li>
          <li>Supplying curriculum books, notebooks and stationery to children in government schools in Jaipur.</li>
        </ul>
      </PolicySection>

      <PolicySection title="How your donations help">
        <p>
          Donations received through this website are voluntary contributions intended to support the social welfare
          objectives described above. Every contribution — one-time or monthly — helps us plan and deliver these
          initiatives and respond to genuine need.
        </p>
        <p>
          Donors receive an acknowledgement of their contribution by email. For receipts, donation records or any
          question about how funds are used, please contact us — we are happy to help.
        </p>
      </PolicySection>

      <PolicySection title="Organisation Information">
        <div className="bg-card border border-border rounded-xl p-5 space-y-2 not-prose">
          <p className="text-base"><span className="text-muted-foreground">Organisation Name:</span> <strong>Findivine</strong> (One Kind Earth Foundation)</p>
          {settings?.ngo_darpan_id && (
            <p className="text-base" data-testid="about-ngo-darpan">
              <span className="text-muted-foreground">NGO Darpan Unique ID:</span> <strong>{settings.ngo_darpan_id}</strong>
            </p>
          )}
          <p className="text-base">
            <span className="text-muted-foreground">Registered Address:</span> Vaishali Nagar, Jaipur, Rajasthan 302021
          </p>
          <p className="text-sm text-muted-foreground">
            Findivine is listed on{" "}
            <a href="https://ngodarpan.gov.in/" target="_blank" rel="noopener noreferrer" data-testid="about-ngodarpan-link" className="text-primary underline underline-offset-2">
              NGO Darpan (ngodarpan.gov.in)
            </a>
            , the official NGO portal of NITI Aayog, Government of India.
          </p>
        </div>
      </PolicySection>

      <PolicySection title="Transparency">
        <p>
          We are committed to transparent and accountable operations. Organisational details, registration
          information and donation records are shared with donors, banks and authorities on legitimate request.
        </p>
      </PolicySection>

      <PolicySection title="Contact">
        <p>
          Email: <a href="mailto:info@findivine.org" data-testid="about-email" className="text-primary underline underline-offset-2">info@findivine.org</a>
          <br />
          Mobile: <a href="tel:+918875285537" data-testid="about-phone" className="text-primary underline underline-offset-2">+91 8875285537</a>
        </p>
      </PolicySection>
    </PageShell>
  );
}
